import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ExcelCell, ExcelExportParams, ExcelRow, ExcelStyle, GridApi, IRowNode } from 'ag-grid-community';
import { ExcelWorkbookReader } from './excel-workbook-reader';

// =====================================
// SHARED TYPES
// =====================================

export enum ExcelViewType {
  MU = 'MU',
  CT = 'CT'
}

// =====================================
// IMPORT INTERFACES
// =====================================

export interface ExcelImportOptions {
  gridApi: GridApi;
  columnApi: GridApi;
  gridContext: any;
  gridOptions?: any;
  errorToastLife?: number;
  currentEntityView?: string;
  suppressSuccessMessage?: boolean;  // Let the caller handle success message
}

/**
 * A cell an import actually changed. `RowNode.setDataValue` reports whether the value
 * differed, so this excludes cells the file simply re-stated.
 *
 * Callers need this because AG Grid delivers `cellValueChanged` to the framework through
 * its async event queue: anything that has to react to what an import changed as soon as
 * the import resolves cannot wait for those events to land.
 */
export interface ImportedCellRef {
  node: IRowNode<any>;
  colId: string;
}

export interface ExcelImportResult {
  success: boolean;
  errorType?: 'invalidFiletype' | 'reading' | 'parsing' | 'intervalMismatch' | 'structureMismatch' | 'general';
  errorMessage?: string;
  updatedCells?: number;
  /** The subset of `updatedCells` whose value actually differed from the grid's. */
  changedCells?: ImportedCellRef[];
  validationDetails?: {
    expectedColumns?: number;
    importedColumns?: number;
    expectedRows?: number;
    importedRows?: number;
  };
}

// =====================================
// EXPORT INTERFACES
// =====================================

export interface ExcelExportOptions {
  gridApi: GridApi;
  columnApi?: GridApi;
  planName: string;
  username: string;
  viewMode: string;  // 'DAY' | 'WEEK' | 'MONTH'
  currentEntityView: string;  // 'CT' | 'MU'
  entityIdentifier: string;  // CT ID or skill name
  sheetName?: string;
  errorToastLife?: number;

  // Callbacks from component
  processHeaderCallback?: (params: any) => string;
  processCellCallback: (params: any) => any;
  shouldRowBeSkipped?: (params: any) => boolean;
}

export interface ExcelExportResult {
  success: boolean;
  errorMessage?: string;
  fileName?: string;
}

// =====================================
// EXCEL STYLE DEFINITIONS
// =====================================

/** ag-Grid's own style type — covers every fill pattern and border key it supports. */
export type ExcelStyleDefinition = ExcelStyle;

/**
 * Not provided in root: must be provided by the component whose template
 * hosts the p-toast outlet, so toasts resolve that component's MessageService.
 */
@Injectable()
export class ExcelService {

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private workbookReader: ExcelWorkbookReader
  ) {}

  // =====================================
  // PUBLIC EXPORT METHODS
  // =====================================

  /**
   * Export grid data to Excel file
   */
  public exportToExcel(options: ExcelExportOptions): ExcelExportResult {
    try {
      const fileName = this.generateExcelFileName(options);

      const exParam: ExcelExportParams = {
        processHeaderCallback: options.processHeaderCallback,
        allColumns: true,
        skipRowGroups: options.currentEntityView === ExcelViewType.MU,
        fileName: fileName,
        sheetName: options.sheetName || 'Plan Data',
        prependContent: this.generateExcelHeader(options),
        processCellCallback: options.processCellCallback,
        shouldRowBeSkipped: options.shouldRowBeSkipped
      };

      options.gridApi.exportDataAsExcel(exParam);

      return { success: true, fileName };
    } catch (error) {
      console.error('Error during Excel export:', error);
      this.showErrorMessage('plan.export.error.general', options.errorToastLife, error);
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * Get standard Excel styles for plan data
   * Can be used to configure gridOptions.excelStyles
   */
  public getExcelStyles(): ExcelStyleDefinition[] {
    return [
      {
        id: 'calculated-field',
        font: { color: '#2196F3' }, // Blue color for calculated fields
        alignment: { vertical: 'Center' },
        dataType: 'Number'
      },
      {
        id: 'capped-field',
        font: { color: '#FF9800' }, // Orange color for capped fields
        alignment: { vertical: 'Center' },
        dataType: 'Number'
      },
      {
        id: 'negative-value',
        font: { color: '#F44336' }, // Red color for negative values
        alignment: { vertical: 'Center' },
        dataType: 'Number'
      },
      {
        id: 'header',
        font: { bold: true },
        alignment: { horizontal: 'Center', vertical: 'Center' },
        interior: { pattern: 'Solid', color: '#F8F8F8' },
        borders: {
          borderBottom: { color: '#E0E0E0', lineStyle: 'Continuous', weight: 1 }
        },
        dataType: 'String'
      },
      {
        id: 'cell-editable',
        interior: { pattern: 'Solid', color: '#ffffff' },
      },
      {
        id: 'cell-readonly',
        interior: { pattern: 'Solid', color: '#F2F2F2' },
      },
      // Must stay after cell-editable/cell-readonly: when a cell matches several
      // styles ag-Grid merges them in this array's order, so the last fill wins.
      // Diagonal grey stripes mirror the on-screen closed-day hatching in grid.scss.
      {
        id: 'closed-field',
        interior: { pattern: 'ThinDiagStripe', color: '#909090', patternColor: '#FFFFFF' },
        dataType: 'String'
      },
      {
        id: 'partial-field',
        font: { italic: true },
        alignment: { vertical: 'Center' }
      },
      {
        id: 'locked',
        font: { color: '#757575' },
        alignment: { vertical: 'Center' },
        dataType: 'String'
      },
      {
        id: 'headerGroup',
        font: { bold: true },
        alignment: { horizontal: 'Center', vertical: 'Center' },
        interior: { pattern: 'Solid', color: '#F0F0F0' },
        dataType: 'String'
      },
      {
        id: 'question-marks',
        dataType: 'String',
      },
      {
        id: 'protected-header',
        font: { bold: true, color: '#000080' }, // Bold blue text to indicate protected
        alignment: { horizontal: 'Left', vertical: 'Center' },
        interior: { pattern: 'Solid', color: '#E6F3FF' }, // Light blue background
        dataType: 'String',
      }
    ];
  }

  /**
   * Generate a standardized Excel filename
   */
  public generateExcelFileName(options: {
    planName: string;
    currentEntityView: string;
    entityIdentifier: string;
    viewMode: string;
  }): string {
    return `${options.planName} - ${this.getViewTypeLabel(options.currentEntityView)} ${options.entityIdentifier}- ${this.getIntervalLabel(options.viewMode)}.xlsx`;
  }

  /**
   * Localized name of an interval, e.g. DAY -> "Day"
   */
  private getIntervalLabel(viewMode: string): string {
    const properCase = (p: string) => p.charAt(0).toUpperCase() + p.substring(1).toLowerCase();
    return this.translate.instant('plan.viewMode.' + properCase(viewMode));
  }

  /**
   * Tenant-aware name of a view type: CT/MU on IEX, WEM Skill/Scheduling Unit on CXone.
   * The per-tenant translation files supply the right wording for each.
   */
  private getViewTypeLabel(currentEntityView: string): string {
    return this.translate.instant(
      currentEntityView === ExcelViewType.CT ? 'label.ct.short' : 'label.mu.short'
    );
  }

  /**
   * Generate Excel header rows with metadata. Each label sits in the same cell as its
   * value so the pair survives the round-trip back through import validation.
   */
  private generateExcelHeader(options: ExcelExportOptions): ExcelRow[] {
    const dateTime = new Date(Date.now()).toISOString();
    const labelled = (key: string, value: string) => this.headerCell(`${this.translate.instant(key)}: ${value}`);

    return [
      {
        cells: [
          this.headerCell(options.planName),
          labelled('plan.excel.header.exportedby', options.username),
          labelled('plan.excel.header.exportdate', dateTime),
          labelled('plan.excel.header.interval', this.getIntervalLabel(options.viewMode)),
          labelled('plan.excel.header.viewtype', this.getViewTypeLabel(options.currentEntityView))
        ]
      } as ExcelRow
    ];
  }

  private headerCell(value: string): ExcelCell {
    return { data: { type: 'String', value }, styleId: 'protected-header' } as ExcelCell;
  }

  // =====================================
  // PUBLIC IMPORT METHODS
  // =====================================

  /**
   * Import data from an Excel file into the grid
   */
  public async importExcelFile(file: File, options: ExcelImportOptions): Promise<ExcelImportResult> {
    // .xlsx only: the legacy .xls binary format is no longer parsed.
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedTypes.includes(file.type) &&
        !file.name.toLowerCase().endsWith('.xlsx')) {

      this.showErrorMessage('plan.import.error.invalidfiletype', options.errorToastLife);
      return {success: false, errorType: 'invalidFiletype'};
    }

    let rows: string[][];
    try {
      rows = await this.workbookReader.readFirstSheet(file);
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      this.showErrorMessage('plan.import.error.parsing', options.errorToastLife, error);
      return {success: false, errorType: 'parsing', errorMessage: error.message};
    }

    if (rows.length === 0) {
      this.showErrorMessage('plan.import.error.emptyfile', options.errorToastLife);
      return {success: false, errorType: 'general'};
    }

    try {
      return this.validateAndApplyImportedData(rows, options);
    } catch (error) {
      console.error('Error processing imported workbook:', error);
      this.showErrorMessage('plan.import.error.processing', options.errorToastLife, error);
      return {success: false, errorType: 'general', errorMessage: error.message};
    }
  }

  // =====================================
  // PRIVATE IMPORT METHODS
  // =====================================

  private validateAndApplyImportedData(data: any[][], options: ExcelImportOptions): ExcelImportResult {
    try {
      // Skip the header row(s) - look for the actual data start
      const dataStartIndex = this.findDataStartIndex(data);

      if (dataStartIndex === -1) {
        this.showErrorMessage('plan.import.error.novaliddata', options.errorToastLife);
        return { success: false, errorType: 'general' };
      }

      const headerRow = data[dataStartIndex];
      const dataRows = data.slice(dataStartIndex + 1);

      // Validate interval match
      if (!this.validateIntervalMatch(data, options.gridContext)) {
        this.showErrorMessage('plan.import.error.interval.mismatch', options.errorToastLife);
        return { success: false, errorType: 'intervalMismatch' };
      }

      // Validate view type match if available
      if (options.currentEntityView) {
        if (!this.validateViewTypeMatch(data, options.currentEntityView)) {
          this.showErrorMessage('plan.import.error.viewtype.mismatch', options.errorToastLife);
          return { success: false, errorType: 'structureMismatch' };
        }
      }

      // Validate structure match
      const structureValidation = this.validateStructureMatch(headerRow, dataRows, options);
      if (!structureValidation.isValid) {
        this.showDetailedStructureError(structureValidation.details, options.errorToastLife);
        return {
          success: false,
          errorType: 'structureMismatch',
          validationDetails: structureValidation.details
        };
      }

      // Apply the imported data to the grid
      const changedCells: ImportedCellRef[] = [];
      const updatedCells = this.applyImportedDataToGrid(dataRows, options, changedCells);

      // Show success message unless caller wants to handle it
      if (!options.suppressSuccessMessage) {
        this.showSuccessMessage('plan.import.success', options.errorToastLife);
      }

      return { success: true, updatedCells, changedCells };

    } catch (error) {
      console.error('Error validating and applying imported data:', error);
      this.showErrorMessage('plan.import.error.validation', options.errorToastLife, error);
      return { success: false, errorType: 'general', errorMessage: error.message };
    }
  }

  private findDataStartIndex(_data: any[][]): number {
    // Row 0: Metadata header (plan name, exported by, date, interval, view type)
    // Row 1: Column headers (Parameter, dates, etc.)
    // Row 2+: Data rows
    // So the column header row is always at index 1
    return 1;
  }

  private validateIntervalMatch(data: any[][], gridContext: any): boolean {
    const imported = this.findHeaderValue(data, ['plan.excel.header.interval', 'Interval']);
    if (imported === null) {
      // No interval in the header, assume it matches
      return true;
    }
    const current = gridContext.viewMode.toString();
    return this.headerValueMatches(imported, current, this.getIntervalLabel(current));
  }

  private validateViewTypeMatch(data: any[][], currentEntityView: string): boolean {
    const imported = this.findHeaderValue(data, ['plan.excel.header.viewtype', 'View Type']);
    if (imported === null) {
      // No view type in the header, assume it matches
      return true;
    }
    return this.headerValueMatches(imported, currentEntityView, this.getViewTypeLabel(currentEntityView));
  }

  /**
   * Read the value of a "Label: value" pair out of the metadata header row. Labels are
   * tried as translation keys first, then as literal text, so a file exported under a
   * different locale still resolves. Returns null when the label carries no value.
   */
  private findHeaderValue(data: any[][], labelKeys: string[]): string | null {
    const prefixes = labelKeys
      .map(key => {
        const translated = this.translate.instant(key);
        return (translated === key ? key : translated).toLowerCase() + ':';
      });

    for (const row of data.slice(0, 5)) {
      for (const cell of row || []) {
        const text = String(cell ?? '').trim();
        const prefix = prefixes.find(p => text.toLowerCase().startsWith(p));
        if (prefix) {
          const value = text.substring(prefix.length).trim();
          if (value) {
            return value;
          }
        }
      }
    }
    return null;
  }

  /**
   * A header value matches if it is either the canonical code (DAY, CT) written by
   * older exports or the display label this tenant and locale currently produce.
   */
  private headerValueMatches(imported: string, canonical: string, label: string): boolean {
    const normalized = imported.trim().toLowerCase();
    return normalized === canonical.trim().toLowerCase() ||
      normalized === label.trim().toLowerCase();
  }

  private validateStructureMatch(headerRow: any[], dataRows: any[][], options: ExcelImportOptions): { isValid: boolean, details: any } {
    if (!headerRow || !dataRows || dataRows.length === 0) {
      return {
        isValid: false,
        details: {
          expectedColumns: 0,
          importedColumns: 0,
          expectedRows: 0,
          importedRows: 0
        }
      };
    }

    // Get current grid structure
    const displayedColumns = options.columnApi.getAllDisplayedColumns();
    const rowNodes: IRowNode<any>[] = [];

    // Add all pinned top rows first
    const pinnedTopRowCount = options.gridApi.getPinnedTopRowCount();
    for (let i = 0; i < pinnedTopRowCount; i++) {
      const pinnedNode = options.gridApi.getPinnedTopRow(i);
      if (pinnedNode) {
        rowNodes.push(pinnedNode);
      }
    }
    //remove the first row from the rowNodes array
    rowNodes.shift();

    // Add regular rows
    options.gridApi.forEachNodeAfterFilterAndSort((rowNode) => {
      if (!rowNode.group) {
        rowNodes.push(rowNode);
      }
    });


    // Get counts
    const expectedColumns = displayedColumns.length;
    const importedColumns = headerRow.filter(column => column !== '').length;
    const expectedRows = rowNodes.length;
    const importedRows = dataRows.length;

    const details = {
      expectedColumns,
      importedColumns,
      expectedRows,
      importedRows
    };

    // For MU view, we need to account for the first column being the group name
    if (options.currentEntityView === 'MU') {
      // MU view exports include an extra first column for group names, so imported columns should be expectedColumns + 1
      const expectedColumnsWithGroupName = expectedColumns + 1;
      if (importedColumns !== expectedColumnsWithGroupName) {

        return { isValid: false, details };
      }
    } else {
      // For CT view, require exact column match
      if (expectedColumns !== importedColumns) {

        return { isValid: false, details };
      }
    }

    // Check if row count exactly matches
    if (expectedRows !== importedRows) {

      return { isValid: false, details };
    }

    return { isValid: true, details };
  }

  private applyImportedDataToGrid(
    mappedData: any[][],
    options: ExcelImportOptions,
    changedCells?: ImportedCellRef[]
  ): number {
    if (!options.gridApi || !options.columnApi) {
      console.error('Grid API not available');
      return 0;
    }


    let updatedCellsCount = 0;
    const displayedColumns = options.columnApi.getAllDisplayedColumns();

    // Get all visible row nodes
    const rowNodes: IRowNode<any>[] = [];

    // Add all pinned top rows first
    const pinnedTopRowCount = options.gridApi.getPinnedTopRowCount();
    for (let i = 1; i < pinnedTopRowCount; i++) {
      const pinnedNode = options.gridApi.getPinnedTopRow(i);
      if (pinnedNode) {
        rowNodes.push(pinnedNode);
      }
    }

    // Add regular rows
    options.gridApi.forEachNodeAfterFilterAndSort((rowNode) => {
      if (!rowNode.group) { // Skip group rows
        rowNodes.push(rowNode);
      }
    });

    // Process each imported row by index
    for (let rowIndex = 0; rowIndex < mappedData.length && rowIndex < rowNodes.length; rowIndex++) {
      const importedRowData = mappedData[rowIndex];
      const targetRowNode = rowNodes[rowIndex];

      // For MU view, skip the first column which contains the group name
      let startColumnIndex = 0;
      if (options.currentEntityView === 'MU') {
        // Skip the first column which contains the group name in MU view
        startColumnIndex = 1;
      }

      // Process each column by index, starting from the appropriate position
      for (let colIndex = startColumnIndex; colIndex < importedRowData.length && (colIndex - startColumnIndex) < displayedColumns.length; colIndex++) {
        const cellValue = importedRowData[colIndex];
        const targetColumnIndex = colIndex - startColumnIndex;
        const targetColumn = displayedColumns[targetColumnIndex];

        // Skip empty cells or if column doesn't exist
        if (cellValue === null || cellValue === undefined || cellValue === '' || !targetColumn) {
          continue;
        }

        // Check if the specific cell is editable
        const colDef = targetColumn.getColDef();
        let isEditable = false;

        if (typeof colDef.editable === 'function') {
          // Call the editable function with the current row data
          isEditable = colDef.editable({
            data: targetRowNode.data,
            node: targetRowNode,
            colDef: colDef,
            column: targetColumn,
            api: options.gridApi,
            columnApi: options.columnApi as any,
            context: options.gridOptions?.context
          });
        } else {
          // Use the boolean value
          isEditable = colDef.editable === true;
        }

        // Skip if cell is not editable
        if (!isEditable) {
          continue;
        }

        try {
          // Use setDataValue to trigger validation and formatting. It returns whether the
          // value actually differed, which is the only synchronous signal that a cell
          // changed - the matching `cellValueChanged` event arrives a task later.
          const colId = targetColumn.getColId();
          const valueChanged = targetRowNode.setDataValue(colId, cellValue);
          updatedCellsCount++;
          if (valueChanged && changedCells) {
            changedCells.push({ node: targetRowNode, colId });
          }
        } catch (error) {
          console.warn(`Error setting value at row ${rowIndex}, col ${colIndex}:`, error);
        }
      }
    }

    // Refresh the grid to ensure all changes are displayed
    options.gridApi.refreshCells();


    return updatedCellsCount;
  }

  // =====================================
  // PRIVATE UTILITY METHODS
  // =====================================

  private showErrorMessage(messageKey: string, life?: number, error?: any): void {
    // Just show the user-friendly message without technical error details
    const detail = this.translate.instant(messageKey);

    // Log technical details to console for debugging, but don't show to user
    if (error) {
      console.error('ExcelService technical error details:', error);
    }

    this.messageService.add({
      severity: 'error',
      detail: detail,
      life: life || 4000
    });
  }

  private showSuccessMessage(messageKey: string, life?: number): void {
    const detail = this.translate.instant(messageKey);
    this.messageService.add({
      severity: 'success',
      detail: detail,
      life: life || 4000
    });
  }

  private showDetailedStructureError(details: any, life?: number): void {
    // Use a simple, user-friendly message instead of verbose details
    let message = this.translate.instant('plan.import.error.structure.simple');

    // If the translation doesn't exist, fall back to a default message
    if (message === 'plan.import.error.structure.simple') {
      message = 'The imported file structure does not match the current view. Please ensure you are importing a file that was exported from the same view.';
    }

    this.messageService.add({
      severity: 'error',
      detail: message,
      life: life || 4000 // Standard display time
    });
  }
}
